import { Box, Flex, Heading, List, ListItem } from '@chakra-ui/react';
import { ArrowRightIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { ContentContainer } from '~/components/container';
import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { SanityImage } from '~/components/sanity-image';
import { trackEvent } from '~/features/analytics/track-event';
import { SanityImageType } from '~/features/page-template/models/image';
import { useCourseLink } from '~/features/program/hooks/use-course-link';

export type ProjectsSectionProps = {
  title: string;
  projects: Array<{
    image: SanityImageType;
    description: string;
    program: {
      key: string;
      slug: string;
    };
  }>;
  programTitles?: Record<string, string | null>;
};

export function ProjectsSection({ title, projects, programTitles }: ProjectsSectionProps) {
  return (
    <Box
      as='section'
      sx={{
        base: '40px 0px',
        md: '80px 0px',
      }}
    >
      <ContentContainer>
        <Heading
          as='h2'
          textAlign='center'
          mb={{
            base: 6,
            md: 12,
          }}
          size={{
            base: 'marketing-3xl',
            md: 'marketing-4xl',
          }}
        >
          {title}
        </Heading>
        <List
          display='grid'
          gridTemplateColumns='repeat(auto-fill, minmax(352px, 1fr))'
          gap={{
            base: 10,
            md: 8,
          }}
        >
          {projects.map((project) => (
            <ProjectCard
              key={project.program.key}
              programTitle={programTitles?.[project.program.key] ?? ''}
              project={project}
            />
          ))}
        </List>
      </ContentContainer>
    </Box>
  );
}

function ProjectCard({
  project,
  programTitle,
}: {
  project: ProjectsSectionProps['projects'][number];
  programTitle: string;
}) {
  const courseLink = useCourseLink({ courseSlug: project.program.slug });

  return (
    <ListItem display='flex'>
      <Flex as='article' flexDir='column'>
        <Box
          borderRadius='6px 18px'
          mb={6}
          overflow='hidden'
          position='relative'
          width='100%'
          height={{
            base: '190px',
            md: '220px',
          }}
        >
          <SanityImage
            alt=''
            image={project.image}
            style={{
              pointerEvents: 'none',
              objectFit: 'cover',
            }}
            fill
          />
        </Box>
        <Flex
          flex='1'
          flexDir='column'
          px={{
            base: 0,
            md: 4,
          }}
        >
          <HeadingWithEmphasis
            text={project.description}
            emphasisProps={{
              fontWeight: '600',
              color: 'blue.500',
            }}
            headingProps={{
              as: 'p',
              fontSize: '1rem',
              lineHeight: '1.5rem',
              fontFamily: 'body',
              fontWeight: '400',
              mb: 6,
            }}
          />
          <ButtonLink
            buttonProps={{
              rightIcon: <ArrowRightIcon h='24px' w='24px' />,
              size: 'icon-right',
              width: { base: '100%', md: 'fit-content' },
              marginTop: 'auto',
              onClick: () =>
                trackEvent({
                  type: 'click',
                  name: 'Careers Page CTA Clicked',
                  label: 'See Course',
                  value: programTitle,
                }),
            }}
            linkProps={{
              href: courseLink,
            }}
          >
            See Course
          </ButtonLink>
        </Flex>
      </Flex>
    </ListItem>
  );
}
